import React, { useMemo, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CheckCircle, Phone, Mail, Car, UserCog, Trash, RotateCcw, Calendar, Star, Send, TrendingUp, Clock } from "lucide-react";
import { StatCard, FilterPill, StatusBadge, SectionCard, ActionButton, EmptyState, APPOINTMENT_STATUS } from "./AdminUI";
import { toast } from "sonner";
import WhatsAppReadyButton from "./WhatsAppReadyButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewRequestsPanel = ({ onRefresh }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/review-requests/stats`);
      setStats(r.data);
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runBatch = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/review-requests/send`);
      if (r.data.sent_count > 0) {
        toast.success(`★ ${r.data.sent_count} demande(s) d'avis envoyée(s)`);
      } else {
        toast.info(`Aucune demande à envoyer aujourd'hui (fenêtre 3 jours : ${r.data.target_date_lookup})`);
      }
      await load();
      onRefresh && onRefresh();
    } catch {
      toast.error("Erreur lors de l'envoi groupé");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#141414] border border-white/[0.06] rounded-xl p-4 animate-pulse h-24" data-testid="review-panel-loading" />
    );
  }
  if (error || !stats) {
    return (
      <div
        className="bg-red-500/[0.05] border border-red-500/20 rounded-xl p-4 flex items-center justify-between gap-3"
        data-testid="review-panel-error"
      >
        <div>
          <p className="text-red-400 text-sm font-medium">Impossible de charger les statistiques d&apos;avis</p>
          <p className="text-white/40 text-[11px] mt-0.5">Vérifie ta connexion ou la session admin.</p>
        </div>
        <button
          onClick={load}
          data-testid="review-panel-retry"
          className="px-3 py-1.5 rounded-md bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 text-xs font-medium"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-yellow-500/[0.05] via-transparent to-transparent border border-yellow-500/20 rounded-xl p-4"
      data-testid="review-requests-panel"
    >
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="text-white font-bold text-sm flex items-center gap-2">
            <Star size={16} className="text-yellow-400 fill-yellow-400" />
            Avis Google — Demandes envoyées
          </h3>
          <p className="text-white/40 text-[11px] mt-0.5">
            Suivi des emails de demande d&apos;avis post-intervention (envoi auto à J+3)
          </p>
        </div>
        <button
          onClick={runBatch}
          disabled={sending}
          data-testid="review-panel-run-batch"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20 hover:border-yellow-500/50 transition-colors text-xs font-medium disabled:opacity-50"
          title="Envoyer maintenant à tous les clients éligibles (RDV terminés il y a 3 jours)"
        >
          <Send size={12} />
          {sending ? "Envoi..." : "Envoyer maintenant"}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <div className="bg-black/30 border border-white/[0.06] rounded-lg p-3" data-testid="stat-sent-total">
          <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Total envoyés</p>
          <p className="text-white text-xl font-bold flex items-center gap-1.5">
            <Send size={14} className="text-yellow-400" />
            {stats.sent_total}
          </p>
        </div>
        <div className="bg-black/30 border border-white/[0.06] rounded-lg p-3" data-testid="stat-sent-30d">
          <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">30 derniers jours</p>
          <p className="text-white text-xl font-bold flex items-center gap-1.5">
            <TrendingUp size={14} className="text-yellow-400" />
            {stats.sent_30d}
          </p>
        </div>
        <div className="bg-black/30 border border-white/[0.06] rounded-lg p-3" data-testid="stat-eligible-now">
          <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Éligibles maintenant</p>
          <p className="text-white text-xl font-bold flex items-center gap-1.5">
            <Clock size={14} className="text-orange-400" />
            {stats.eligible_now}
          </p>
        </div>
        <div className="bg-black/30 border border-white/[0.06] rounded-lg p-3" data-testid="stat-google-count">
          <p className="text-white/40 text-[10px] uppercase tracking-wider mb-1">Avis reçus (Google)</p>
          <p className="text-white text-xl font-bold flex items-center gap-1.5">
            <Star size={14} className="text-emerald-400 fill-emerald-400" />
            {stats.google_reviews_count ?? "—"}
            {stats.google_rating > 0 && (
              <span className="text-emerald-400 text-xs font-normal ml-1">
                {String(stats.google_rating).replace(".", ",")}/5
              </span>
            )}
          </p>
        </div>
      </div>

      {stats.eligible_now > 0 && (
        <p className="text-white/50 text-[11px] mt-3">
          💡 <strong className="text-yellow-400">{stats.eligible_now}</strong> RDV{stats.eligible_now > 1 ? "s" : ""} terminé{stats.eligible_now > 1 ? "s" : ""} avec un email client attendent une demande d&apos;avis.
        </p>
      )}
    </div>
  );
};

const CompletedTab = ({ ctx }) => {
  const {
    completedAppointments, filterStatus, setFilterStatus, handleReconfirm, handleDeletePermanent,
    handleSendReviewRequest,
    formatDate,
  } = ctx;

  const list = useMemo(() => {
    const arr = completedAppointments || [];
    if (filterStatus === "all") return arr;
    return arr.filter((a) => a.status === filterStatus);
  }, [completedAppointments, filterStatus]);

  const counts = {
    total: (completedAppointments || []).length,
    pending: (completedAppointments || []).filter((a) => a.status === "pending").length,
    confirmed: (completedAppointments || []).filter((a) => a.status === "confirmed").length,
    cancelled: (completedAppointments || []).filter((a) => a.status === "cancelled").length,
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <CheckCircle size={20} className="text-emerald-400" />
            Rendez-vous effectués
          </h2>
          <p className="text-white/50 text-xs mt-0.5">{list.length} rendez-vous passés ou annulés</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {[
            { key: "all", label: "Tous" },
            { key: "pending", label: "En attente" },
            { key: "confirmed", label: "Confirmés" },
            { key: "cancelled", label: "Annulés" },
          ].map((s) => (
            <FilterPill key={s.key} active={filterStatus === s.key} onClick={() => setFilterStatus(s.key)}>
              {s.label}
            </FilterPill>
          ))}
        </div>
      </div>

      {/* Review Requests Panel (Google reviews dashboard) */}
      <ReviewRequestsPanel onRefresh={ctx.fetchAllAppointments} />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Calendar} label="Total effectués" value={counts.total} color="text-white" accent="bg-white/[0.06]" />
        <StatCard icon={CheckCircle} label="Confirmés" value={counts.confirmed} color="text-emerald-400" accent="bg-emerald-500/10" />
        <StatCard icon={CheckCircle} label="En attente" value={counts.pending} color="text-amber-400" accent="bg-amber-500/10" />
        <StatCard icon={RotateCcw} label="Annulés" value={counts.cancelled} color="text-red-400" accent="bg-red-500/10" />
      </div>

      {/* List */}
      {list.length === 0 ? (
        <EmptyState icon={CheckCircle} title="Aucun rendez-vous passé" />
      ) : (
        <div className="space-y-2">
          {list.map((apt) => {
            const cfg = APPOINTMENT_STATUS[apt.status] || APPOINTMENT_STATUS.pending;
            const isCancelled = apt.status === "cancelled";
            return (
              <div
                key={apt.id}
                className={`bg-[#141414] border rounded-xl p-4 flex flex-wrap gap-4 items-start hover:border-white/[0.12] transition-colors ${
                  isCancelled ? "border-red-500/20 opacity-70" : "border-white/[0.06]"
                }`}
              >
                {/* Date */}
                <div className={`px-3 py-2 rounded-lg text-center min-w-[88px] ${
                  isCancelled ? "bg-red-500/10 text-red-400" : "bg-white/[0.04] text-white/70"
                }`}>
                  <p className="text-[10px] uppercase tracking-wider opacity-90">{formatDate(apt.date)}</p>
                  <p className="text-xl font-bold leading-tight">{apt.time}</p>
                  <p className="text-[10px] opacity-70 mt-0.5">{apt.duration || 60} min</p>
                </div>

                {/* Service */}
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-white font-semibold">{apt.service_name}</p>
                    <StatusBadge config={cfg} />
                  </div>
                  <p className="text-white/60 text-sm mt-1">{apt.client_name}</p>
                  <div className="flex items-center gap-1.5 text-[#FF3B30] font-semibold text-[13px] mt-0.5">
                    <Car size={12} /> {apt.client_plate || "N/A"}
                  </div>
                  {apt.technician && (
                    <div className="flex items-center gap-1.5 text-cyan-400 text-xs mt-0.5">
                      <UserCog size={11} /> {apt.technician}
                    </div>
                  )}
                </div>

                {/* Contact */}
                <div className="min-w-[180px] space-y-1">
                  <div className="flex items-center gap-1.5 text-white/50 text-xs flex-wrap">
                    <Phone size={11} /> {apt.client_phone}
                    <WhatsAppReadyButton apt={apt} testId={`completed-whatsapp-btn-${apt.id}`} />
                  </div>
                  <div className="flex items-center gap-1.5 text-white/50 text-xs">
                    <Mail size={11} /> {apt.client_email || "N/A"}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-1.5 flex-wrap">
                  {!isCancelled && apt.client_email && (
                    apt.review_request_sent ? (
                      <div
                        data-testid={`review-sent-badge-${apt.id}`}
                        title="Demande d'avis Google déjà envoyée"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium"
                      >
                        <Star size={12} className="fill-emerald-400" />
                        Avis demandé
                      </div>
                    ) : (
                      <button
                        data-testid={`send-review-request-${apt.id}`}
                        onClick={() => handleSendReviewRequest(apt.id)}
                        title="Envoyer une demande d'avis Google au client"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 hover:bg-yellow-500/20 hover:border-yellow-500/40 transition-colors text-xs font-medium"
                      >
                        <Star size={12} />
                        Demander un avis
                      </button>
                    )
                  )}
                  {apt.status === "cancelled" && (
                    <ActionButton icon={RotateCcw} label="Réactiver" color="green" onClick={() => handleReconfirm(apt.id)} />
                  )}
                  <ActionButton icon={Trash} label="" color="red" onClick={() => handleDeletePermanent(apt.id)} title="Supprimer" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CompletedTab;
